import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Icon } from '@unite-us/app-components';
import DialogV2 from 'common/modal/DialogV2';
import IconButton from 'common/buttons/IconButton/IconButton';
import NoteDeleteContent from './NoteDeleteContent';
import SideDate from './SideDate';
import useNoteDeletion from './useNoteDeletion';
import { NOTE_VISIBILITY } from './constants';
import {
  getInteractionDateParts,
  getNoteDate,
  getProcessedNoteMetadata,
  getServiceProvidedData,
  isNoteDeletable,
  shouldShowDeleteButton,
  shouldShowReason,
} from './noteUtils';

const EMPLOYEE_NOTE_ITEM_CLASS = 'employee_note_item';
const NOTE_ITEM_CLASS = 'note_item';

const ProcessedNoteHeader = ({
  type,
  added,
  employeeName,
  date,
}) => (
  type && date ? (
    <p className="pb-2">
      <strong>{type}</strong>
      {added} by <strong>{employeeName}</strong> on {date}
    </p>
  ) : null
);

ProcessedNoteHeader.propTypes = {
  type: PropTypes.string.isRequired,
  added: PropTypes.string.isRequired,
  employeeName: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
};

const NoteRow = ({ label, value }) => (
  value ? <p className="pb-2">{label}: {value}</p> : null
);

NoteRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

NoteRow.defaultProps = {
  value: '',
};

const ServiceProvidedRows = ({
  serviceProvided,
  serviceProvidedType,
  serviceProvidedAmount,
  serviceProvidedUnit,
}) => (
  serviceProvided ? (
    <div>
      <NoteRow label="Service" value={serviceProvidedType} />
      <NoteRow label="Amount" value={`${serviceProvidedAmount} ${serviceProvidedUnit}`} />
    </div>
  ) : null
);

ServiceProvidedRows.propTypes = {
  serviceProvided: PropTypes.object,
  serviceProvidedType: PropTypes.string.isRequired,
  serviceProvidedAmount: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  serviceProvidedUnit: PropTypes.string.isRequired,
};

ServiceProvidedRows.defaultProps = {
  serviceProvided: null,
};

const NoteText = ({ noteType, renderTextAsHtml, text }) => (
  <>
    <strong className="font-bold pb-2">{noteType}:</strong>
    {/* eslint-disable react/no-danger */}
    {renderTextAsHtml ? (
      <div
        className="whitespace-pre-wrap"
        dangerouslySetInnerHTML={{ __html: text }}
      />
    ) : (
      <p className="whitespace-pre-wrap">{text}</p>
    )}
    {/* eslint-enable react/no-danger */}
  </>
);

NoteText.propTypes = {
  noteType: PropTypes.string.isRequired,
  renderTextAsHtml: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
};

const PrivateVisibilityNotice = ({ visibility }) => (
  visibility === NOTE_VISIBILITY.PRIVATE ? (
    <div className="inline-block flex-1 w-64 align-middle text-right text-medium-gray">
      <Icon icon="EyeDisabled" /> Private to my organization
    </div>
  ) : null
);

PrivateVisibilityNotice.propTypes = {
  visibility: PropTypes.string.isRequired,
};

const DeleteNoteAction = ({
  className,
  deletable,
  showDeleteButton,
  openDeleteDialog,
}) => (
  showDeleteButton && deletable ? (
    <div className="deleteNoteButton">
      <IconButton
        className={`${className}_delete-btn`}
        labelText="Delete Note"
        icon="Delete"
        onClick={openDeleteDialog}
      />
    </div>
  ) : null
);

DeleteNoteAction.propTypes = {
  className: PropTypes.string.isRequired,
  deletable: PropTypes.bool.isRequired,
  showDeleteButton: PropTypes.bool.isRequired,
  openDeleteDialog: PropTypes.func.isRequired,
};

const RegularNote = ({
  currentEmployeeId,
  interactionDate,
  interactionType,
  text,
  duration,
  note,
  noteType,
  category,
  details,
  reason,
  visibility,
  showDeleteNoteIcon,
  renderTextAsHtml,
}) => {
  const {
    day,
    month,
    year,
  } = getInteractionDateParts(interactionDate);
  const {
    type,
    added,
    date,
  } = getProcessedNoteMetadata(note);
  const {
    serviceProvided,
    serviceProvidedType,
    serviceProvidedAmount,
    serviceProvidedUnit,
  } = getServiceProvidedData(details);
  const {
    deleteNoteDialogRef,
    openDeleteDialog,
    cancelDelete,
    confirmDelete,
  } = useNoteDeletion(note);

  const showInteractionType = interactionType !== '';
  const showDate = category !== 'general';
  const noteDate = getNoteDate(category, details);
  const showReasonRow = shouldShowReason(reason, details);
  const deletable = isNoteDeletable(details);
  const showDeleteButton = shouldShowDeleteButton({
    showDeleteNoteIcon,
    currentEmployeeId,
    note,
  });
  const className = showDeleteButton ? EMPLOYEE_NOTE_ITEM_CLASS : NOTE_ITEM_CLASS;
  const employeeName = get(note, 'employee.full_name', '');

  return (
    <div
      className={`flex ${className} p-3 py-4 bg-white border border-solid border-medium-border-blue rounded`}
    >
      <SideDate day={day} month={month} year={year} />
      <div className="inline-block flex-1 w-64 align-middle">
        <ProcessedNoteHeader
          type={type}
          added={added}
          employeeName={employeeName}
          date={date}
        />
        <NoteRow label="Interaction Type" value={showInteractionType ? interactionType : ''} />
        <NoteRow label="Date" value={showDate ? noteDate : ''} />
        <NoteRow label="Duration" value={duration} />
        <ServiceProvidedRows
          serviceProvided={serviceProvided}
          serviceProvidedType={serviceProvidedType}
          serviceProvidedAmount={serviceProvidedAmount}
          serviceProvidedUnit={serviceProvidedUnit}
        />
        <NoteRow label="Reason" value={showReasonRow ? reason : ''} />
        <NoteText
          noteType={noteType}
          renderTextAsHtml={renderTextAsHtml}
          text={text}
        />
      </div>
      <PrivateVisibilityNotice visibility={visibility} />
      <DeleteNoteAction
        className={className}
        deletable={deletable}
        showDeleteButton={showDeleteButton}
        openDeleteDialog={openDeleteDialog}
      />
      <DialogV2
        ref={deleteNoteDialogRef}
        title="Delete Note"
        confirmLabel="Delete"
        cancelHandler={cancelDelete}
        confirmationHandler={confirmDelete}
      >
        <NoteDeleteContent />
      </DialogV2>
    </div>
  );
};

RegularNote.defaultProps = {
  currentEmployeeId: '',
  duration: null,
  noteType: 'Note',
  note: {},
  details: {},
  reason: '',
  showDeleteNoteIcon: false,
  visibility: '',
  renderTextAsHtml: false,
};

RegularNote.propTypes = {
  currentEmployeeId: PropTypes.string,
  interactionDate: PropTypes.object.isRequired,
  interactionType: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  duration: PropTypes.string,
  category: PropTypes.string.isRequired,
  details: PropTypes.object,
  reason: PropTypes.string,
  note: PropTypes.object,
  noteType: PropTypes.string,
  visibility: PropTypes.string,
  showDeleteNoteIcon: PropTypes.bool,
  renderTextAsHtml: PropTypes.bool,
};

export default RegularNote;
